/**
 * @architecture-header 1
 * @layer shared
 * @domain workspace-shell
 * @responsibility Expose translator, toast and viewport helpers the workspace screens call on every render.
 * @state-owner navigation store
 * @allowed-imports React; workspace store; i18n
 * @forbidden network; API client; role ownership
 * @public-api workspace shell and modules
 * @side-effects subscribes to viewport resize
 * @tests workspace-behaviour.test.ts
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { createTranslator, type Translator } from '../lib/i18n';
import { useWorkspaceUiStore } from './workspace-store';

export function useTranslator(): Translator {
  const language = useWorkspaceUiStore((state) => state.language);
  return useMemo(() => createTranslator(language), [language]);
}

export function useNotify(): (message: string) => void {
  const notify = useWorkspaceUiStore((state) => state.notify);
  return useCallback((message: string) => notify(message), [notify]);
}

export function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 900,
  );
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

export function useWorkspacePick(): (selection: string | null) => void {
  const mobile = useIsMobile();
  const pick = useWorkspaceUiStore((state) => state.pick);
  return useCallback((selection: string | null) => pick(selection, mobile), [mobile, pick]);
}

/** Trails `value` by `delayMs`, e.g. so a search query hook does not fire on every keystroke. */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timeout);
  }, [value, delayMs]);
  return debounced;
}
